import { useLocale } from "@/hooks/useLocale";
import { LopDetailNote } from "@/components/payslip/LopDetailNote";
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ChevronLeft } from "lucide-react";
import { format } from "date-fns";
import toast from "react-hot-toast";

import { usePageTitle } from "@/hooks/usePageTitle";
import { withBranding } from "@/utils/helper";
import { PermissionButton } from "@/components/ui/permission-button";
import { PLATFORM_SECTION_ENUM } from "@/utils/enums";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ROUTES } from "@/constants/routes";
import {
  useGetCompanyDisputeQuery,
  useRespondDisputeMutation,
  type DisputeLineDecision,
  type DisputeStatus,
  type IDisputeLine,
} from "@/services/(company)/payrollDisputesApi";
import { DisputeDetailCard } from "@/components/payroll-disputes/DisputeDetailCard";
import { cn } from "@/utils/cn";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";

const STATUS_TONE: Record<DisputeStatus, string> = {
  PENDING: "bg-yellow-50 text-yellow-700",
  UNDER_REVIEW: "bg-blue-50 text-blue-700",
  RESOLVED: "bg-success-100/10 text-success-100",
  REJECTED: "bg-red-50 text-red-600",
};

const buildRespondStatusOptions = (
  t: TFunction,
): { value: DisputeStatus; label: string }[] => [
  { value: "RESOLVED", label: t("company:payrolls.accept_resolve") },
  { value: "REJECTED", label: t("common:buttons.reject") },
];

const buildStatusLabels = (t: TFunction): Record<DisputeStatus, string> => ({
  PENDING: t("common:status.pending"),
  UNDER_REVIEW: t("company:payrolls.under_review"),
  RESOLVED: t("company:payrolls.resolved"),
  REJECTED: t("common:status.rejected"),
});

export const CompanyDisputeDetail = () => {
  const { t } = useTranslation(["company", "common"]);
  const { formatDate } = useLocale();
  usePageTitle(withBranding(t("company:payrolls.dispute_review")));
  const statusLabels = useMemo(() => buildStatusLabels(t), [t]);
  const navigate = useNavigate();
  const { id } = useParams();
  const disputeId = Number(id);
  const skip = !disputeId || Number.isNaN(disputeId);

  const { data, isLoading } = useGetCompanyDisputeQuery(disputeId, { skip });
  const dispute = data?.data;

  const [respondDispute, { isLoading: isResponding }] =
    useRespondDisputeMutation();

  const [newStatus, setNewStatus] = useState<DisputeStatus>("RESOLVED");
  const [hrResponse, setHrResponse] = useState("");
  const [lineDecisions, setLineDecisions] = useState<
    Record<
      number,
      { decision: DisputeLineDecision; hrNote: string; approvedAmount: string }
    >
  >({});

  useEffect(() => {
    if (!dispute) return;
    setHrResponse(dispute.hrResponse ?? "");
    const seed: Record<
      number,
      { decision: DisputeLineDecision; hrNote: string; approvedAmount: string }
    > = {};
    dispute.lines.forEach((l) => {
      seed[l.id] = {
        decision: l.hrDecision,
        hrNote: l.hrNote ?? "",
        // What HR agrees the line should be (B44); starts at the proposal.
        approvedAmount: String(l.approvedAmount ?? l.proposedAmount),
      };
    });
    setLineDecisions(seed);
  }, [dispute]);

  const isFinalized =
    dispute?.status === "RESOLVED" || dispute?.status === "REJECTED";

  const allDecided = useMemo(
    () =>
      dispute?.lines.every(
        (l) => lineDecisions[l.id]?.decision !== "PENDING",
      ) ?? false,
    [dispute, lineDecisions],
  );

  const handleSubmit = async () => {
    if (!dispute) return;
    if (hrResponse.trim().length === 0) {
      toast.error(t("company:payrolls.hr_response_is_required"));
      return;
    }
    const finalStatus =
      newStatus === "RESOLVED" || newStatus === "REJECTED"
        ? newStatus
        : newStatus;
    if (
      (finalStatus === "RESOLVED" || finalStatus === "REJECTED") &&
      !allDecided
    ) {
      toast.error(
        t("company:payrolls.decide_on_every_line_before_resolving_rejecting"),
      );
      return;
    }
    const badAmount = Object.values(lineDecisions).some(
      (v) =>
        v.decision === "ACCEPTED" &&
        (v.approvedAmount.trim() === "" ||
          !Number.isFinite(Number(v.approvedAmount)) ||
          Number(v.approvedAmount) < 0),
    );
    if (badAmount) {
      toast.error(t("company:payroll_disputes.agreed_amount_invalid"));
      return;
    }
    try {
      await respondDispute({
        id: disputeId,
        status: finalStatus,
        hrResponse: hrResponse.trim(),
        lineDecisions: Object.entries(lineDecisions).map(([k, v]) => ({
          disputeLineId: Number(k),
          decision: v.decision,
          hrNote: v.hrNote.trim() || undefined,
          approvedAmount:
            v.decision === "ACCEPTED" && v.approvedAmount.trim() !== ""
              ? Math.round(Number(v.approvedAmount) * 100) / 100
              : undefined,
        })),
      }).unwrap();
      toast.success(t("company:payrolls.dispute_updated"));
    } catch (err: any) {
      const msg = err?.data?.message;
      toast.error(
        Array.isArray(msg)
          ? msg.join(", ")
          : (msg ?? t("company:payrolls.failed")),
      );
    }
  };

  if (isLoading) {
    return (
      <div className="p-4 md:p-6 text-sm text-gray-400 text-center">
        {t("my-space:ui.loading_dispute")}
      </div>
    );
  }
  if (!dispute) {
    return (
      <div className="p-4 md:p-6 text-sm text-gray-400 text-center">
        {t("my-space:ui.dispute_not_found")}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => navigate(ROUTES.HOME.COMPANY.PAYROLL.DISPUTES)}
            className="border border-card-background rounded-xs p-2 w-fit h-fit shrink-0"
          >
            <ChevronLeft className="text-primary size-5 rtl-flip" />
          </button>
          <div className="flex flex-col">
            <h1 className="text-lg font-bold text-gray-900">
              {t("company:payrolls.dispute_number", { id: dispute.id })}
            </h1>
            <span className="text-xs text-gray-500">
              {dispute.raisedByUser?.fullName} ·{" "}
              {dispute.payrollRun?.periodStart
                ? format(new Date(dispute.payrollRun.periodStart), "MMM yyyy")
                : ""}{" "}
              ·{" "}
              {t("company:payrolls.raised_on", {
                date: format(new Date(dispute.createdAt), "dd MMM yyyy"),
              })}
            </span>
          </div>
        </div>
        <span
          className={cn(
            "text-[11px] font-semibold px-2 py-1 rounded-xs",
            STATUS_TONE[dispute.status],
          )}
        >
          {statusLabels[dispute.status]}
        </span>
      </div>

      <DisputeDetailCard dispute={dispute} />

      {/* HR Decision Panel */}
      {!isFinalized && (
        <div className="rounded-xs border border-borderSecondary bg-white">
          <div className="px-4 py-3 border-b border-borderSecondary">
            <span className="text-sm font-semibold text-gray-900">
              {t("company:payrolls.hr_decision")}
            </span>
          </div>
          <div className="p-4 flex flex-col gap-4">
            {/* Per-line decisions */}
            <div className="flex flex-col gap-2">
              <span className="text-xs font-medium text-gray-700">
                {t("company:payrolls.decide_on_each_line")}
              </span>
              {dispute.lines.map((l) => (
                <div
                  key={l.id}
                  className="border border-gray-200 rounded-xs p-3 grid grid-cols-12 gap-2"
                >
                  <div className="col-span-12 sm:col-span-4 flex flex-col">
                    <span className="text-xs font-semibold text-gray-900">
                      {/* The server names Loss of Pay in English (B45). */}
                      {l.lopDetail ? t("company:payrolls.loss_of_pay") : l.name}
                    </span>
                    <LopDetailNote detail={l.lopDetail} />
                    {l.disputedDates?.length ? (
                      <span className="text-[11px] text-warning">
                        {t("company:payroll_disputes.disputed_days", {
                          dates: l.disputedDates
                            .map((d) => {
                              const [y, m, day] = d.split("-").map(Number);
                              return formatDate(
                                new Date(y, m - 1, day),
                                "EEE d MMM",
                              );
                            })
                            .join(t("company:payslip.lop_separator")),
                        })}
                      </span>
                    ) : null}
                    <span className="text-[11px] text-gray-500">
                      {t(
                        `company:payroll_disputes.line_type_${l.lineType.toLowerCase()}`,
                      )}{" "}
                      · {l.currentAmount.toFixed(2)} →{" "}
                      {l.proposedAmount.toFixed(2)} ({l.diff > 0 ? "+" : ""}
                      {l.diff.toFixed(2)})
                    </span>
                  </div>
                  <div className="col-span-6 sm:col-span-3">
                    <Select
                      value={lineDecisions[l.id]?.decision ?? "PENDING"}
                      onValueChange={(v) =>
                        setLineDecisions((prev) => ({
                          ...prev,
                          [l.id]: {
                            ...(prev[l.id] ?? {
                              decision: "PENDING",
                              hrNote: "",
                              approvedAmount: String(l.proposedAmount),
                            }),
                            decision: v as DisputeLineDecision,
                          },
                        }))
                      }
                    >
                      <SelectTrigger className="h-8 text-xs">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="PENDING">
                          {t("common:status.pending")}
                        </SelectItem>
                        <SelectItem value="ACCEPTED">
                          {t("company:payrolls.accept")}
                        </SelectItem>
                        <SelectItem value="REJECTED">
                          {t("common:buttons.reject")}
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="col-span-6 sm:col-span-5">
                    <input
                      type="text"
                      value={lineDecisions[l.id]?.hrNote ?? ""}
                      onChange={(e) =>
                        setLineDecisions((prev) => ({
                          ...prev,
                          [l.id]: {
                            ...(prev[l.id] ?? {
                              decision: "PENDING",
                              hrNote: "",
                              approvedAmount: String(l.proposedAmount),
                            }),
                            hrNote: e.target.value,
                          },
                        }))
                      }
                      placeholder={t("company:payrolls.hr_note_optional")}
                      maxLength={1000}
                      className="w-full h-8 text-xs px-2 border border-gray-200 rounded-xs focus:outline-none focus:ring-2 focus:ring-blue-200"
                    />
                  </div>
                  {lineDecisions[l.id]?.decision === "ACCEPTED" && (
                    <AgreedAmountField
                      line={l}
                      value={lineDecisions[l.id].approvedAmount}
                      onChange={(value) =>
                        setLineDecisions((prev) => ({
                          ...prev,
                          [l.id]: { ...prev[l.id], approvedAmount: value },
                        }))
                      }
                    />
                  )}
                </div>
              ))}
            </div>

            {/* Overall response + status */}
            <div className="grid grid-cols-1 sm:grid-cols-[1fr_220px] gap-3">
              <div className="flex flex-col gap-1.5">
                <label className="text-xs font-medium text-gray-700">
                  {t("company:payrolls.overall_hr_response")}{" "}
                  <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={hrResponse}
                  onChange={(e) => setHrResponse(e.target.value)}
                  placeholder={t(
                    "company:payrolls.summary_message_to_the_employee",
                  )}
                  rows={3}
                  maxLength={2000}
                  className="text-sm px-3 py-2 border border-gray-200 rounded-xs resize-none focus:outline-none focus:ring-2 focus:ring-blue-200"
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <label className="text-xs font-medium text-gray-700">
                  {t("company:payrolls.final_status")}
                </label>
                <Select
                  value={newStatus}
                  onValueChange={(v) => setNewStatus(v as DisputeStatus)}
                >
                  <SelectTrigger className="h-10">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {buildRespondStatusOptions(t).map((o) => (
                      <SelectItem key={o.value} value={o.value}>
                        {o.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <PermissionButton
                  section={PLATFORM_SECTION_ENUM.PAYROLL}
                  action="edit"
                  mode="hide"
                  onClick={handleSubmit}
                  disabled={isResponding || !hrResponse.trim()}
                >
                  {isResponding
                    ? t("common:states.submitting")
                    : t("company:payrolls.submit_decision")}
                </PermissionButton>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * The amount HR agrees a line should be (B44) and what that pays: the
 * dispute settles from this, not from the employee's proposal.
 */
const AgreedAmountField = ({
  line,
  value,
  onChange,
}: {
  line: IDisputeLine;
  value: string;
  onChange: (value: string) => void;
}) => {
  const { t } = useTranslation(["company"]);
  const { formatNumber } = useLocale();
  const agreed = Number(value);
  const diff = Number.isFinite(agreed) ? agreed - line.currentAmount : 0;
  const pays = line.lineType === "DEDUCTION" ? -diff : diff;
  return (
    <div className="col-span-12 flex flex-col gap-1 sm:flex-row sm:items-center sm:gap-3">
      <label className="text-[11px] font-medium text-gray-700">
        {t("company:payroll_disputes.agreed_amount")}
      </label>
      <input
        type="number"
        step="0.01"
        min={0}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="h-8 w-36 rounded-xs border border-gray-200 px-2 text-end text-xs focus:outline-none focus:ring-2 focus:ring-blue-200"
      />
      <span
        className={cn(
          "text-[11px] font-semibold",
          pays > 0
            ? "text-success"
            : pays < 0
              ? "text-danger"
              : "text-muted-foreground",
        )}
      >
        {t("company:payroll_disputes.agreed_amount_pays", {
          amount: `${pays > 0 ? "+" : ""}${formatNumber(pays, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })}`,
        })}
      </span>
    </div>
  );
};
